import { supabase, type Priority, type Status } from './api'
import type { LinkRow } from './linksApi'

/**
 * Admin-only calls. Every one takes the caller's token and re-checks admin
 * rights inside the database (`require_admin`), so being able to reach these
 * functions is not the same as being allowed to use them. Verified over HTTPS:
 * a staff token is refused by all of them.
 */

function fail(error: { code?: string; message?: string }): Error {
  if (error.code === '42501') {
    return new Error(error.message || 'You do not have permission to do that.')
  }
  if (error.code === '22023') return new Error(error.message || 'That value is not allowed.')
  return new Error(error.message || 'Something went wrong. Try again.')
}

export interface Assignee {
  /** Needed so an admin can tick off their own row from the merged task list. */
  assignment_id: string
  employee_id: string
  full_name: string
  status: Status
  note: string | null
  completed_at: string | null
}

export interface AdminTask {
  task_id: string
  title: string
  priority: Priority
  due_date: string
  notes: string | null
  archived: boolean
  days_until: number
  done_count: number
  total_count: number
  assignees: Assignee[]
  /** Set when this task was spawned from a reported issue. Null otherwise. */
  issue_id: string | null
  issue_title: string | null
  /** URLs attached to this task. Empty array when there are none. */
  links: LinkRow[]
  /** Set when this task was created from a headline (0019). Null otherwise. */
  headline_id: string | null
  headline_body: string | null
  /** Live (unarchived) issues raised from this task. */
  raised_issue_count: number
}

export interface AdminEmployee {
  employee_id: string
  full_name: string
  email: string | null
  slack_user_id: string | null
  is_admin: boolean
  /** Can make and remove admins, and manage other admins' accounts (0023). */
  is_super_admin: boolean
  active: boolean
  pin_set: boolean
  open_tasks: number
  /**
   * Null for another admin's row unless you are a super admin: an admin's link
   * skips the PIN, so handing it to a plain admin would let them become that
   * person (0023).
   */
  link_token: string | null
}

export type Role = 'staff' | 'admin' | 'super_admin'

export function roleOf(p: Pick<AdminEmployee, 'is_admin' | 'is_super_admin'>): Role {
  return p.is_super_admin ? 'super_admin' : p.is_admin ? 'admin' : 'staff'
}

export async function adminListTasks(
  token: string,
  includeArchived = false,
): Promise<AdminTask[]> {
  const { data, error } = await supabase.rpc('admin_list_tasks', {
    p_token: token,
    p_include_archived: includeArchived,
  })
  if (error) throw fail(error)
  return (data as AdminTask[] | null) ?? []
}

export async function adminCreateTask(
  token: string,
  task: {
    title: string
    priority: Priority
    dueDate: string
    notes: string | null
    employeeIds: string[]
    /** Links the task back to the issue it came out of. Null for a plain task. */
    issueId?: string | null
    /** Links the task back to the headline it was created from (0019). */
    headlineId?: string | null
  },
): Promise<string> {
  const { data, error } = await supabase.rpc('admin_create_task', {
    p_token: token,
    p_title: task.title,
    p_priority: task.priority,
    // Sent as a plain YYYY-MM-DD string into a `date` column. Dates carry no
    // timezone, so there is no UTC-shift to get wrong here — unlike timestamps.
    p_due_date: task.dueDate,
    p_notes: task.notes,
    p_employee_ids: task.employeeIds,
    p_issue_id: task.issueId ?? null,
    p_headline_id: task.headlineId ?? null,
  })
  if (error) throw fail(error)
  return data as string
}

export async function adminUpdateTask(
  token: string,
  taskId: string,
  task: { title: string; priority: Priority; dueDate: string; notes: string | null },
): Promise<void> {
  const { error } = await supabase.rpc('admin_update_task', {
    p_token: token,
    p_task_id: taskId,
    p_title: task.title,
    p_priority: task.priority,
    p_due_date: task.dueDate,
    p_notes: task.notes,
  })
  if (error) throw fail(error)
}

export async function adminSetAssignees(
  token: string,
  taskId: string,
  employeeIds: string[],
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_assignees', {
    p_token: token,
    p_task_id: taskId,
    p_employee_ids: employeeIds,
  })
  if (error) throw fail(error)
}

export async function adminArchiveTask(
  token: string,
  taskId: string,
  archived: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('admin_archive_task', {
    p_token: token,
    p_task_id: taskId,
    p_archived: archived,
  })
  if (error) throw fail(error)
}

export async function adminListEmployees(token: string): Promise<AdminEmployee[]> {
  const { data, error } = await supabase.rpc('admin_list_employees', { p_token: token })
  if (error) throw fail(error)
  return (data as AdminEmployee[] | null) ?? []
}

export async function adminAddEmployee(
  token: string,
  person: { fullName: string; email: string | null; slackUserId: string | null },
): Promise<string> {
  const { data, error } = await supabase.rpc('admin_add_employee', {
    p_token: token,
    p_full_name: person.fullName,
    p_email: person.email,
    p_slack_user_id: person.slackUserId,
  })
  if (error) throw fail(error)
  return data as string
}

export async function adminUpdateEmployee(
  token: string,
  employeeId: string,
  person: {
    fullName: string
    email: string | null
    slackUserId: string | null
    active: boolean
  },
): Promise<void> {
  const { error } = await supabase.rpc('admin_update_employee', {
    p_token: token,
    p_employee_id: employeeId,
    p_full_name: person.fullName,
    p_email: person.email,
    p_slack_user_id: person.slackUserId,
    p_active: person.active,
  })
  if (error) throw fail(error)
}

/** Returns the NEW token. The person's old link stops working immediately. */
export async function adminReissueLink(token: string, employeeId: string): Promise<string> {
  const { data, error } = await supabase.rpc('admin_reissue_link', {
    p_token: token,
    p_employee_id: employeeId,
  })
  if (error) throw fail(error)
  return data as string
}

/**
 * Super admins only. Making someone an admin also replaces their link and
 * clears their PIN: the old staff link may be on any device that ever tapped
 * their name, and an admin's link skips the PIN. They need a PIN set before
 * they can sign in again.
 */
export async function adminSetRole(token: string, employeeId: string, role: Role): Promise<void> {
  const { error } = await supabase.rpc('admin_set_role', {
    p_token: token,
    p_employee_id: employeeId,
    p_role: role,
  })
  if (error) throw fail(error)
}

/** Pass null to clear a PIN entirely, which locks that admin out until a new one is set. */
export async function adminSetPin(
  token: string,
  employeeId: string,
  newPin: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_pin', {
    p_token: token,
    p_employee_id: employeeId,
    p_new_pin: newPin,
  })
  if (error) throw fail(error)
}

/** The link to hand someone. Fragment, never query — see token.ts. */
export function personalLink(linkToken: string): string {
  return `${window.location.origin}/#t=${linkToken}`
}
